/**
 * Product Form Custom Element
 * <product-form> - Handles variant selection, quantity, and add to cart
 */

class ProductForm extends HTMLElement {
  constructor() {
    super();
    // Don't query DOM here - element not attached yet
    this.productData = null;
    this.currentVariant = null;
    this.options = [];
    this._boundHandlers = {};
  }

  connectedCallback() {
    // Query DOM after element is attached
    this.cacheElements();
    this.loadProductData();
    this.bindEvents();
  }

  disconnectedCallback() {
    // Cleanup event listeners
    if (this._boundHandlers.variantChanged) {
      document.removeEventListener('variant:changed', this._boundHandlers.variantChanged);
    }
  }

  cacheElements() {
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.addToCartBtn = this.querySelector('[data-add-to-cart]');
    this.addToCartText = this.querySelector('[data-add-to-cart-text]');
    this.buyNowBtn = this.querySelector('[data-buy-now]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.quantityMinus = this.querySelector('[data-quantity-minus]');
    this.quantityPlus = this.querySelector('[data-quantity-plus]');
    this.optionButtons = this.querySelectorAll('[data-option-buttons]');
    this.optionSelects = this.querySelectorAll('[data-option-select]');
  }

  loadProductData() {
    const productJson = document.querySelector('[data-product-json]');
    if (!productJson) return;

    try {
      this.productData = JSON.parse(productJson.textContent);
      if (this.variantInput) {
        this.currentVariant = this.productData.variants.find(
          v => v.id === parseInt(this.variantInput.value)
        );
      }
      this.initOptions();
    } catch (e) {
      console.error('[ProductForm] Error parsing product JSON:', e);
    }
  }

  initOptions() {
    if (!this.productData || !this.currentVariant) return;

    this.options = [];
    const optionFieldsets = this.querySelectorAll('[data-option-index]');
    
    optionFieldsets.forEach((fieldset) => {
      const index = parseInt(fieldset.dataset.optionIndex);
      this.options[index - 1] = this.currentVariant.options[index - 1];
    });
  }

  bindEvents() {
    // Option buttons (variant buttons & color swatches)
    this.optionButtons.forEach(container => {
      container.addEventListener('click', this.onOptionButtonClick.bind(this));
    });

    // Option selects (dropdowns)
    this.optionSelects.forEach(select => {
      select.addEventListener('change', this.onSelectChange.bind(this));
    });

    // Quantity buttons
    this.quantityMinus?.addEventListener('click', () => this.updateQuantity(-1));
    this.quantityPlus?.addEventListener('click', () => this.updateQuantity(1));
    this.quantityInput?.addEventListener('change', () => this.validateQuantity());

    // Form submit
    this.form?.addEventListener('submit', this.onSubmit.bind(this));

    // Buy now
    this.buyNowBtn?.addEventListener('click', this.onBuyNow.bind(this));

    // Listen for variant changes from other components
    this._boundHandlers.variantChanged = this.onExternalVariantChange.bind(this);
    document.addEventListener('variant:changed', this._boundHandlers.variantChanged);
  }

  // ==================== Event Handlers ====================

  onOptionButtonClick(e) {
    const btn = e.target.closest('[data-option-value]');
    if (!btn || btn.classList.contains('is-disabled')) return;

    const optionIndex = parseInt(btn.dataset.optionIndex);
    const optionValue = btn.dataset.optionValue;

    // Update options array
    this.options[optionIndex - 1] = optionValue;

    // Update active state
    const container = btn.closest('[data-option-buttons]');
    container.querySelectorAll('[data-option-value]').forEach(b => {
      b.classList.toggle('is-active', b === btn);
    });

    // Update displayed value
    this.updateOptionDisplay(btn.closest('[data-option-index]'), optionValue);

    // Find and update variant
    this.findVariant();
  }

  onSelectChange(e) {
    const select = e.target;
    const optionIndex = parseInt(select.dataset.optionIndex);
    const optionValue = select.value;

    // Update options array
    this.options[optionIndex - 1] = optionValue;

    // Update displayed value
    this.updateOptionDisplay(select.closest('[data-option-index]'), optionValue);

    // Find and update variant
    this.findVariant();
  }

  onExternalVariantChange(e) {
    const { variant, sectionId } = e.detail || {};
    // Ignore if event came from this component
    if (sectionId === this.dataset.sectionId) return;
    if (variant) {
      this.updateVariant(variant, false);
    }
  }

  async onSubmit(e) {
    e.preventDefault();
    if (!this.currentVariant?.available) return;

    this.setLoading(true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: this.currentVariant.id,
            quantity: parseInt(this.quantityInput?.value || 1)
          }]
        })
      });

      const result = await response.json();

      if (response.ok) {
        this.dispatchEvent(new CustomEvent('cart:item-added', {
          bubbles: true,
          detail: { items: result.items }
        }));
        document.dispatchEvent(new CustomEvent('cart:added', {
          detail: { items: result.items }
        }));
        this.updateCartCount();
        this.showNotification('Added to cart!', 'success');
      } else {
        throw new Error(result.description || 'Error adding to cart');
      }
    } catch (error) {
      console.error('[ProductForm] Add to cart error:', error);
      this.showNotification(error.message, 'error');
    } finally {
      this.setLoading(false);
    }
  }

  onBuyNow() {
    if (!this.currentVariant?.available) return;
    const quantity = parseInt(this.quantityInput?.value || 1);
    window.location.href = `/cart/${this.currentVariant.id}:${quantity}`;
  }

  // ==================== Variant Logic ====================

  findVariant() {
    if (!this.productData) return;

    const variant = this.productData.variants.find(v => {
      return v.options.every((opt, i) => opt === this.options[i]);
    });

    if (variant) {
      this.updateVariant(variant, true);
    } else {
      this.setUnavailable();
    }
  }

  updateVariant(variant, dispatchEvent = true) {
    this.currentVariant = variant;
    
    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    this.updatePrice(variant);
    this.updateAvailability(variant);
    this.updateSku(variant);
    this.updateUrl(variant);

    if (dispatchEvent) {
      document.dispatchEvent(new CustomEvent('variant:changed', {
        detail: {
          variant,
          sectionId: this.dataset.sectionId
        }
      }));
    }
  }

  // ==================== UI Updates ====================

  updateOptionDisplay(fieldset, value) {
    const valueSpan = fieldset?.querySelector('.the4-option-value');
    if (valueSpan) {
      valueSpan.textContent = value;
    }
  }

  updatePrice(variant) {
    const wrapper = document.querySelector('[data-product-price-wrapper]');
    if (!wrapper) return;

    const priceEl = wrapper.querySelector('[data-product-price]');
    const compareEl = wrapper.querySelector('.the4-price-compare');
    const badgeEl = wrapper.querySelector('.the4-sale-badge');
    const container = wrapper.querySelector('.the4-product-price');

    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (priceEl) {
      priceEl.textContent = this.formatMoney(variant.price);
    }

    container?.classList.toggle('the4-product-price--on-sale', onSale);

    if (compareEl) {
      compareEl.textContent = onSale ? this.formatMoney(variant.compare_at_price) : '';
      compareEl.hidden = !onSale;
    }

    if (badgeEl) {
      if (onSale) {
        const percent = Math.round(
          ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
        );
        badgeEl.textContent = `-${percent}%`;
      }
      badgeEl.hidden = !onSale;
    }
  }

  updateAvailability(variant) {
    const el = document.querySelector('[data-product-availability]');
    if (el) {
      el.innerHTML = variant.available
        ? `<span class="the4-availability the4-availability--in-stock">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            In stock
          </span>`
        : `<span class="the4-availability the4-availability--out-of-stock">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            Out of stock
          </span>`;
    }

    this.setButtonState(variant.available);
  }

  updateSku(variant) {
    const skuContainer = document.querySelector('[data-product-sku]');
    const skuValue = skuContainer?.querySelector('.the4-product-sku-value');
    
    if (skuValue) {
      skuValue.textContent = variant.sku || '';
    }
    if (skuContainer) {
      skuContainer.hidden = !variant.sku;
    }
  }

  updateUrl(variant) {
    if (!history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());
  }

  setUnavailable() {
    this.setButtonState(false, 'Unavailable');
  }

  setButtonState(available, text) {
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = !available;
    }
    if (this.addToCartText) {
      this.addToCartText.textContent = text || (available ? 'Add to cart' : 'Sold out');
    }
    if (this.buyNowBtn) {
      this.buyNowBtn.disabled = !available;
    }
  }

  setLoading(loading) {
    this.addToCartBtn?.classList.toggle('is-loading', loading);
  }

  // ==================== Quantity ====================

  updateQuantity(change) {
    if (!this.quantityInput) return;
    const current = parseInt(this.quantityInput.value) || 1;
    this.quantityInput.value = Math.max(1, current + change);
  }

  validateQuantity() {
    if (!this.quantityInput) return;
    const value = parseInt(this.quantityInput.value);
    if (isNaN(value) || value < 1) {
      this.quantityInput.value = 1;
    }
  }

  // ==================== Helpers ====================

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (e) {
      console.error('[ProductForm] Error updating cart count:', e);
    }
  }

  showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `the4-notification the4-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      padding: 16px 24px;
      background: ${type === 'success' ? '#43a047' : '#e53935'};
      color: white;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      z-index: 9999;
      animation: the4SlideIn 0.3s ease;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'the4SlideOut 0.3s ease forwards';
      notification.addEventListener('animationend', () => notification.remove());
    }, 3000);
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || '$';
    return currency === '$' ? `$${amount}` : `${currency} ${amount}`;
  }
}

// Inject keyframe animations
if (!document.querySelector('#the4-product-form-styles')) {
  const style = document.createElement('style');
  style.id = 'the4-product-form-styles';
  style.textContent = `
    @keyframes the4SlideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    @keyframes the4SlideOut {
      from { transform: translateX(0); opacity: 1; }
      to { transform: translateX(100%); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
}

// Register custom element
if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
